import React, { useEffect, useState } from 'react'
import { DashboardPageProps } from './DashboardPage.types'
import styles from './DashboardPage.module.scss'
import cl from 'classnames'
import { Button, CurrencySelector, PageHeader, SectionContainer } from 'components/shared'
import { IconActionHistory, IconDashboard, IconSettings } from 'assets'
import { Clock } from './Clock'
import { ActionHistoryTable, HighlightsSection } from 'components'
import { OverviewStats } from 'pages/statistics'
import { ViewersStats } from 'pages/statistics/ViewersStats'
import { useGetIsResponsiveScreen, User } from 'utilities'
import { SectionHeader } from 'pages/statistics/common'
import { DashboardCards } from './DashboardCards'
import { LayoutDialog } from './LayoutDialog'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import { DASHBOARD_PAGE_STORE, SectionLabels } from 'store'

const DashboardPageComponent = (props: DashboardPageProps) => {
  const {
    className,
    style,
    location: { pathname },
    history: { replace, push },
    [DASHBOARD_PAGE_STORE]: {
      orderedLayoutSectionLabels
    }
  } = props

  const { isResponsive: isLargeMobile } = useGetIsResponsiveScreen('(max-width: 920px)')

  useEffect(() => {
    if (pathname === '/') replace('/dashboard')
  }, [pathname, replace])

  const [showLayoutDialog, setShowLayoutDialog] = useState<boolean>(false)

  const getUIForSection = (label: SectionLabels) => {
    switch (label) {
      case SectionLabels.STATS_HIGHLIGHTS: {
        return (
          <HighlightsSection
            key={label}
            labelProps={{
              label: 'Statistic highlights*',
              labelBold: true,
              showIcon: true
            }}
            style={{
              marginTop: 20
            }}
          />
        )
      }
      case SectionLabels.STATS_CARDS: {
        return (
          <DashboardCards
            key={label}
            style={{
              marginTop: 20
            }}
          />
        )
      }
      case SectionLabels.STATS_OVERVIEW: {
        return (
          <OverviewStats
            key={label}
            renderDashboardVersion
            style={{
              marginTop: 20
            }}
          />
        )
      }
      case SectionLabels.STATS_VIEWERS: {
        return (
          <ViewersStats
            key={label}
            renderDashboardVersion
            style={{
              marginTop: 20
            }}
          />
        )
      }
      case SectionLabels.ACTION_HISTORY: {
        return (
          <SectionContainer
            key={label}
            style={{
              marginTop: 20
            }}
          >
            <SectionHeader
              label='Action history'
              boldLabel
              labelIcon={IconActionHistory}
            />
            <ActionHistoryTable
              userId={User.getId()}
              tableContainerProps={{
                noBoxShadow: true,
                style: { marginTop: 22, overflow: 'auto' }
              }}
              paginationProps={{
                page: 0,
                size: 5
              }}
              hidePaginationInfo
              viewAllButtonProps={{
                onClick: () => push('/action-history')
              }}
              isDashboardVersion={true}
            />
          </SectionContainer>
        )
      }
      default: return null
    }
  }

  return (
    <div
      className={cl(styles.pageContainer, styles.scrollable, className)}
      style={{ ...style, overflowX: 'hidden' }}
    >
      <PageHeader
        navSegments={[
          {
            label: 'Dashboard',
            icon: IconDashboard
          }
        ]}
        buttonsContainerChildren={(
          <div
            className={cl(
              styles.toolbar,
              isLargeMobile && styles.toolbarMobile
            )}
          >
            <CurrencySelector />
            <div className={styles.verticalLine} aria-hidden />
            <Clock />
            <div className={styles.verticalLine} aria-hidden />
            <Button
              preset='icon'
              className={styles.settingsButton}
              onClick={() => setShowLayoutDialog(s => !s)}
            >
              <IconSettings />
            </Button>
          </div>
        )}
      />

      {orderedLayoutSectionLabels.map(getUIForSection)}

      {showLayoutDialog && (
        <LayoutDialog
          closeDialog={() => setShowLayoutDialog(false)}
        />
      )}
    </div>
  )
}

const DashboardPage = inject(
  DASHBOARD_PAGE_STORE
)(observer(DashboardPageComponent))
export { DashboardPage }
